import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { axiosAuth } from './AuthContext';
import { useAuth } from './AuthContext';

const CreditsContext = createContext({
  credits: 0,
  costTrack: 2,
  costBookAudio: 5,
  lowThreshold: 10,
  loading: false,
  showBuyModal: false,
  openBuyModal: () => {},
  closeBuyModal: () => {},
  insufficientFor: null,
  refreshBalance: async () => {},
  spend: async () => ({ ok: false }),
});

export const useCredits = () => useContext(CreditsContext);

export const CreditsProvider = ({ children }) => {
  const { user } = useAuth();
  const [credits, setCredits] = useState(0);
  const [costTrack, setCostTrack] = useState(2);
  const [costBookAudio, setCostBookAudio] = useState(5);
  const [lowThreshold, setLowThreshold] = useState(10);
  const [loading, setLoading] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [insufficientFor, setInsufficientFor] = useState(null);

  const refreshBalance = useCallback(async () => {
    if (!user) {
      setCredits(0);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axiosAuth.get('/api/credits/balance');
      setCredits(data.credits || 0);
      setCostTrack(data.stream_cost_track || 2);
      setCostBookAudio(data.stream_cost_book_audio || 5);
      setLowThreshold(data.low_threshold || 10);
    } catch (e) {
      /* swallow — user might not be logged in */
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  // Re-poll balance after a successful Stripe redirect
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('credits_purchase') === 'success') {
      // Small delay so the checkout/status endpoint can settle the grant
      setTimeout(refreshBalance, 1500);
      setTimeout(refreshBalance, 4000);
    }
  }, [refreshBalance]);

  const openBuyModal = useCallback((reason = null) => {
    setInsufficientFor(reason);
    setShowBuyModal(true);
  }, []);

  const closeBuyModal = useCallback(() => {
    setShowBuyModal(false);
    setInsufficientFor(null);
  }, []);

  /**
   * Attempt to spend credits for a stream. Returns {ok: true, deducted, credits, reason}
   * on success (including admin_free / owned_free) or {ok: false, insufficient: true, ...}
   * when the user is short on credits — in which case the buy modal opens automatically.
   */
  const spend = useCallback(async (kind, resourceId) => {
    if (!user) return { ok: false, notLoggedIn: true };
    try {
      const { data } = await axiosAuth.post('/api/credits/spend', { kind, resource_id: resourceId });
      setCredits(data.credits ?? credits);
      return { ok: true, ...data };
    } catch (err) {
      if (err.response?.status === 402) {
        const detail = err.response.data?.detail || {};
        openBuyModal({ kind, needed: detail.needed, currentCredits: detail.credits });
        return { ok: false, insufficient: true, ...detail };
      }
      return { ok: false, error: err.message };
    }
  }, [user, credits, openBuyModal]);

  const value = useMemo(() => ({
    credits, costTrack, costBookAudio, lowThreshold, loading,
    showBuyModal, openBuyModal, closeBuyModal, insufficientFor,
    refreshBalance, spend,
  }), [credits, costTrack, costBookAudio, lowThreshold, loading,
       showBuyModal, openBuyModal, closeBuyModal, insufficientFor,
       refreshBalance, spend]);

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
};

export default CreditsContext;
